import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetCompanyAudiencesQuery,
  useCreateCompanyAudienceMutation,
  useUpdateCompanyAudienceMutation,
  useDeleteCompanyAudienceMutation,
  CompanyAudience,
} from "../../store/apis/companyApi";
import * as Sentry from "@sentry/react";
import { AudienceDialog, AudienceFormState } from "./AudienceDialog";

interface AudienceManagementProps {
  companyId: string;
  readOnly?: boolean;
}

const AudienceManagement: React.FC<AudienceManagementProps> = ({
  companyId,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { data: audiencesData, isLoading } = useGetCompanyAudiencesQuery(
    companyId,
    { skip: !companyId }
  );
  const audiences = audiencesData?.data ?? [];

  const [createAudience, { isLoading: isCreating }] =
    useCreateCompanyAudienceMutation();
  const [updateAudience, { isLoading: isUpdating }] =
    useUpdateCompanyAudienceMutation();
  const [deleteAudience] = useDeleteCompanyAudienceMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [selectedAudience, setSelectedAudience] =
    useState<CompanyAudience | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setDialogMode("create");
    setSelectedAudience(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (audience: CompanyAudience) => {
    setDialogMode("edit");
    setSelectedAudience(audience);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAudience(null);
  };

  const handleSubmit = async (formData: AudienceFormState) => {
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        sex: formData.sex,
        minimumAge: formData.minimumAge
          ? Number(formData.minimumAge)
          : undefined,
        maximumAge: formData.maximumAge
          ? Number(formData.maximumAge)
          : undefined,
        language:
          formData.languages.length > 0
            ? formData.languages.join(",")
            : undefined,
        geography: formData.geography.trim() || undefined,
        technology: formData.technology.trim() || undefined,
        interests: formData.interests.trim() || undefined,
        dataAudience: formData.dataAudience.trim() || undefined,
        ctr: formData.ctr ? Number(formData.ctr) : undefined,
        exclusions: formData.exclusions.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        imageUrl: formData.imageUrl.trim() || undefined,
      };

      if (dialogMode === "create") {
        await createAudience({
          companyId,
          audience: payload,
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t("company.audiences.createSuccess"),
            severity: "success",
          })
        );
      } else if (selectedAudience) {
        await updateAudience({
          audienceId: selectedAudience.id,
          updates: {
            ...payload,
            description: payload.description || null,
          },
        }).unwrap();
        dispatch(
          setToastMessage({
            message: t("company.audiences.updateSuccess"),
            severity: "success",
          })
        );
      }
      handleCloseDialog();
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message:
            dialogMode === "create"
              ? t("company.audiences.createError")
              : t("company.audiences.updateError"),
          severity: "error",
        })
      );
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteAudience(id).unwrap();
      dispatch(
        setToastMessage({
          message: t("company.audiences.deleteSuccess"),
          severity: "success",
        })
      );
    } catch (error) {
      Sentry.captureException(error);
      dispatch(
        setToastMessage({
          message: t("company.audiences.deleteError"),
          severity: "error",
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        p: 2,
        mb: 0.5,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: audiences.length > 0 ? 2 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleAltOutlinedIcon
            sx={{ color: theme.palette.primary.main, fontSize: 20 }}
          />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Nunito",
              fontWeight: 700,
              color: theme.palette.grey[900],
            }}
          >
            {t("company.audiences.title")}
          </Typography>
          {audiences.length > 0 && (
            <Chip
              label={audiences.length}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }}
            />
          )}
        </Box>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              borderRadius: 50,
              textTransform: "none",
              fontFamily: "Nunito",
              fontWeight: 700,
            }}
          >
            {t("company.audiences.add")}
          </Button>
        )}
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && audiences.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: theme.palette.grey[500], fontStyle: "italic", mt: 2 }}
        >
          {t("company.audiences.empty")}
        </Typography>
      )}

      {/* Audience list */}
      {!isLoading && audiences.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {audiences.map((audience) => (
            <Accordion
              key={audience.id}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: "8px !important",
                boxShadow: "none",
                "&:before": { display: "none" },
                overflow: "hidden",
                mb: 1,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  p: 1.5,
                  minHeight: "48px !important",
                  "& .MuiAccordionSummary-content": {
                    m: 0,
                    justifyContent: "space-between",
                    alignItems: "center",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {audience.name}
                  </Typography>
                  {audience.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.grey[600],
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {audience.description}
                    </Typography>
                  )}
                </Box>
                {!readOnly && (
                  <Box
                    sx={{ display: "flex", gap: 0.5, ml: 1, flexShrink: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(audience)}
                      sx={{ color: theme.palette.grey[500] }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(audience.id)}
                      disabled={deletingId === audience.id}
                      sx={{ color: theme.palette.error.main }}
                    >
                      {deletingId === audience.id ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 2,
                  borderTop: `1px solid ${theme.palette.grey[100]}`,
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Grid container spacing={2}>
                  {/* Demographics */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={700}
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {t("company.audiences.sex", "Sex")}
                    </Typography>
                    <Typography variant="body2">
                      {audience.sex === "BOTH"
                        ? t("company.audiences.sexBoth", "Both")
                        : audience.sex === "MAN"
                          ? t("company.audiences.sexMan", "Men")
                          : t("company.audiences.sexWoman", "Women")}
                    </Typography>
                  </Grid>
                  {audience.minimumAge && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.minimumAge", "Minimum Age")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.minimumAge}
                      </Typography>
                    </Grid>
                  )}
                  {audience.maximumAge && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.maximumAge", "Maximum Age")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.maximumAge}
                      </Typography>
                    </Grid>
                  )}
                  {audience.language && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.language", "Language")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.language}
                      </Typography>
                    </Grid>
                  )}
                  {audience.geography && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.geography", "Geography")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.geography}
                      </Typography>
                    </Grid>
                  )}
                  {audience.technology && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.technology", "Technology")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.technology}
                      </Typography>
                    </Grid>
                  )}
                  {audience.interests && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.interests", "Interests")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.interests}
                      </Typography>
                    </Grid>
                  )}
                  {audience.dataAudience && (
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.dataAudience", "Data Audience")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.dataAudience}
                      </Typography>
                    </Grid>
                  )}
                  {audience.exclusions && (
                    <Grid item xs={12} sm={12}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.exclusions", "Exclusions")}
                      </Typography>
                      <Typography variant="body2">
                        {audience.exclusions}
                      </Typography>
                    </Grid>
                  )}
                  {audience.notes && (
                    <Grid item xs={12} sm={12}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t("company.audiences.notes", "Notes")}
                      </Typography>
                      <Typography variant="body2">{audience.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <AudienceDialog
        open={dialogOpen}
        mode={dialogMode}
        initialData={selectedAudience}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUpdating}
      />
    </Box>
  );
};

export default AudienceManagement;
